// src/components/AddFollowupModal.jsx
//
// Add one follow-up scan to an existing case. The case's baseline is reused
// (it stays pinned). The user picks an interval that hasn't been used yet.

import React, { useRef, useState } from 'react';
import { apiPostForm, ApiError } from '../api';
import { useAuth } from '../contexts/AuthContext';
import './UploadCaseModal.css'; // share styles

const ALL_INTERVALS = [
  { value: 6,  label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
];

const MAX_MB = 50;

function isValidNiftiName(name) {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.endsWith('.nii') || n.endsWith('.nii.gz');
}

export default function AddFollowupModal({ caseId, usedIntervals, onClose, onAdded }) {
  const { user } = useAuth();
  const fileRef = useRef(null);

  const availableIntervals = ALL_INTERVALS.filter(
    (i) => !usedIntervals.includes(i.value)
  );

  const [file, setFile] = useState(null);
  const [interval, setInterval] = useState(availableIntervals[0]?.value || null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  function pickFile(f) {
    if (!f) { setFile(null); return; }
    if (!isValidNiftiName(f.name)) {
      setError(`"${f.name}" — must be a .nii or .nii.gz file.`);
      setFile(null);
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`"${f.name}" is over ${MAX_MB} MB.`);
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  }

  const canSubmit = file && interval && !uploading;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setUploading(true);
    setProgress(0);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('interval_months', String(interval));

    try {
      const result = await apiPostForm(
        `/api/cases/${encodeURIComponent(caseId)}/followups`,
        fd, user,
        (sent, total) => {
          if (total) setProgress(sent / total);
        }
      );
      onAdded?.(result.case);
      onClose?.();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || err.message) : 'Upload failed.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  }

  if (availableIntervals.length === 0) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <header className="modal__head">
            <h2 className="modal__title">All intervals used</h2>
            <button className="modal__close" onClick={onClose}>×</button>
          </header>
          <div className="modal__body">
            <p>This case already has follow-ups at 6, 12, and 24 months.</p>
            <p>To add a different follow-up, delete one of the existing ones first.</p>
            <div className="form__actions">
              <button className="btn btn-primary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h2 className="modal__title">Add a follow-up scan</h2>
          <button className="modal__close" onClick={onClose}>×</button>
        </header>

        <div className="modal__body">
          <p className="modal__lead">
            We'll run TAFNet on the existing baseline plus this new follow-up,
            adding one prediction to your case.
          </p>

          <form onSubmit={handleSubmit} className="form">
            <div className="form__field">
              <label htmlFor="fu_file">Follow-up scan (.nii / .nii.gz)</label>
              <input
                id="fu_file"
                ref={fileRef}
                type="file"
                accept=".nii,.nii.gz,application/gzip"
                onChange={(e) => pickFile(e.target.files?.[0])}
                disabled={uploading}
                required
              />
              {file && (
                <span className="form__filename">
                  {file.name} ({(file.size / (1024*1024)).toFixed(1)} MB)
                </span>
              )}
            </div>

            <div className="form__field">
              <label>Interval (months after baseline)</label>
              <div className="form__radios">
                {availableIntervals.map((i) => (
                  <label key={i.value} className="form__radio">
                    <input
                      type="radio" name="interval" value={i.value}
                      checked={interval === i.value}
                      onChange={() => setInterval(i.value)}
                      disabled={uploading}
                    />
                    {i.label}
                  </label>
                ))}
              </div>
            </div>

            {error && <div className="form__error" role="alert">{error}</div>}

            {uploading && (
              <div className="form__progress">
                <div className="form__progress-track">
                  <div className="form__progress-fill"
                       style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
                <span className="form__progress-label">
                  Uploading… {Math.round(progress * 100)}%
                </span>
              </div>
            )}

            <div className="form__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}
                      disabled={uploading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                {uploading ? 'Uploading…' : 'Add follow-up'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
